/**
 * @file きき分けあそび (epic_24) の子ども向け全画面。
 * @what: /study/:familyId/kikiwake/:personId。和音を鳴らして色ハタで答える 1 セッション
 *   (出題列は進段エンジンが生成) を回し、終わりに StudyProgressLog へ自動保存する。
 *   和音が 1 つの時期 (最初の 2 週間ほど) は当てる場面ではないので、問いかけを
 *   「こえに だして いってみよう」にし、言えたことを褒める (原典 s.81: 最初の和音だけを
 *   2 週間きかせ、色の名前で答えさせる)。2 つめが増えたら通常の問いかけに戻る。
 * @why: 江口式の家庭練習を毎日回すための面 (epic_24 §4.1)。親の同席が前提で、画面には
 *   成績・数字・段階名を出さない (AC-4)。全画面は FullscreenViewer と同じく画面を覆う
 *   要素で作る (iOS Safari は全画面 API を使えない)。
 * @failure: データ読込失敗は文言のみ (詳細は console)。途中離脱は visibilitychange /
 *   pagehide で partial 保存 (同一ログ id の上書きなので、完走時の保存と二重にならない)。
 *   音が出ない事故は開始前の「きこえた」ゲート + 無音疑いの自動ノーカウントで
 *   進段データを守る (§5.2)。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  fetchFamilyMembers,
  learnersOf,
  memberLabel,
  type FamilyMember,
} from "@/features/study/api/members";
import { useHeaderLearner } from "@/shared/ui/headerLearnerContext";
import { todayLocal } from "@/features/study/api/dates";
import {
  fetchProgressLogs,
  fetchStudyPlan,
  newProgressLogId,
  postProgressLog,
  type StudyProgressLogInput,
} from "@/features/study/api/plans";
import { createKikiwakeAudio, type KikiwakeAudio } from "@/features/study/lib/kikiwakeAudio";
import { kikiwakeChord, type ChordId } from "@/features/study/model/kikiwake/chords";
import {
  KIKIWAKE_CURRENT_CFG,
  KIKIWAKE_CURRENT_CONFIG,
} from "@/features/study/model/kikiwake/kikiwakeConfig";
import {
  applyKikiwakeSession,
  buildKikiwakeMemo,
  buildKikiwakeSession,
  detectMutedSuspect,
  KIKIWAKE_MAX_FETCH_PAGES,
  markAllNoCount,
  reinforceAfterMiss,
  resolveKikiwakeState,
  type KikiwakeLogInput,
  type KikiwakeResolvedState,
} from "@/features/study/model/kikiwake/kikiwakeEngine";
import {
  ONKAN_MATERIAL_ID,
  serializeKikiwakeDetail,
  type KikiwakeState,
  type KikiwakeTrial,
} from "@/features/study/model/kikiwake/kikiwakeLog";
import "@/features/study/components/study.css";

const cfg = KIKIWAKE_CURRENT_CONFIG;

type Phase =
  | { kind: "boot" }
  | { kind: "broken" }
  | { kind: "start" }
  | { kind: "soundcheck"; playing: boolean }
  | { kind: "intro"; done: number }
  | { kind: "playing"; qi: number }
  | { kind: "answering"; qi: number }
  | { kind: "praise"; qi: number; chord: ChordId; praise: string }
  | { kind: "teach"; qi: number; chord: ChordId }
  | { kind: "katame" }
  | { kind: "finished" };

interface SessionRef {
  logId: string;
  seq: number;
  base: string | null;
  baseState: KikiwakeState;
  plan: ChordId[];
  katame: ChordId[];
  intro: ChordId | null;
  trials: KikiwakeTrial[];
  startedMs: number;
  enabledAt: number;
  replays: number;
  katameNoticed: boolean;
  savedFinal: boolean;
}

interface Props {
  /** テスト差し替え用。既定は実 Web Audio。 */
  audioFactory?: () => KikiwakeAudio;
  rng?: () => number;
  /** 演出の待ち時間 (テストは 0 にする)。 */
  timings?: { praiseMs: number; teachMs: number; noticeMs: number };
}

const DEFAULT_TIMINGS = { praiseMs: 1300, teachMs: 2400, noticeMs: 1800 };

const PRAISES = ["せいかい！", "すごい！", "やったね！", "そのちょうし！"];

/** 和音が 1 つの時期は「当てる」場面ではないので、言えたことを褒める (原典 s.81)。 */
const FIRST_CHORD_PRAISES = ["よく いえたね！", "そのとおり！", "じょうず！"];

/** チャイルドロックの長押し秒数。子ども画面から親側への遷移ガードは 3 秒長押し
 *  (docs/02.initial_ui_specification.md の親モード遷移方針に整合)。 */
const LONG_PRESS_MS = 3000;

/** 長押しで発火するボタン (3 歳の誤タップで親向け画面に出ない、S5)。 */
function LongPressButton({
  label,
  hint,
  onFire,
}: {
  label: string;
  hint: string;
  onFire: () => void;
}) {
  const timer = useRef<number | null>(null);
  const [holding, setHolding] = useState(false);
  const start = () => {
    setHolding(true);
    timer.current = window.setTimeout(() => {
      setHolding(false);
      onFire();
    }, LONG_PRESS_MS);
  };
  const cancel = () => {
    setHolding(false);
    if (timer.current !== null) window.clearTimeout(timer.current);
  };
  return (
    <button
      type="button"
      className={`kikiwake-longpress${holding ? " is-holding" : ""}`}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
    >
      {label}
      <span className="kikiwake-longpress-hint">{hint}</span>
    </button>
  );
}

export function StudyKikiwakeRoute({
  audioFactory,
  rng = Math.random,
  timings = DEFAULT_TIMINGS,
}: Props = {}) {
  const { familyId = "", personId = "" } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: "boot" });
  const [learnerName, setLearnerName] = useState("");
  // 家族のまなぶ人 (ヘッダーの候補)。選び直すと、その人のきき分けへ移る。
  const [learners, setLearners] = useState<FamilyMember[]>([]);
  const [activeIds, setActiveIds] = useState<ChordId[]>(["domiso"]);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "done" | "failed">("idle");
  const hasMaterialRef = useRef(false);
  const resolvedRef = useRef<KikiwakeResolvedState | null>(null);
  const sessionRef = useRef<SessionRef | null>(null);
  const audioRef = useRef<KikiwakeAudio | null>(null);
  const timerRef = useRef<number | null>(null);

  const audio = useCallback((): KikiwakeAudio => {
    if (!audioRef.current) {
      audioRef.current = audioFactory ? audioFactory() : createKikiwakeAudio(cfg.pitchA);
    }
    return audioRef.current;
  }, [audioFactory]);

  // ---- データ読込: 学習者・plan (教材の有無)・ログ (状態の復元、§6.5) ----
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const members = await fetchFamilyMembers(familyId, controller.signal);
      const candidates = learnersOf(members);
      const learner = candidates.find((m) => m.person_id === personId);
      if (!learner) {
        navigate("/404", { replace: true });
        return;
      }
      setLearners(candidates);
      setLearnerName(memberLabel(learner));
      try {
        const plan = await fetchStudyPlan(familyId, personId, controller.signal);
        hasMaterialRef.current = (plan?.materials ?? []).some((m) => m.id === ONKAN_MATERIAL_ID);
      } catch {
        hasMaterialRef.current = false;
      }
      const logs: KikiwakeLogInput[] = [];
      let kikiwakeSeen = 0;
      let cursor: string | undefined;
      let pages = 0;
      do {
        const page = await fetchProgressLogs(familyId, personId, cursor, controller.signal, 100);
        pages += 1;
        for (const item of page.items) {
          const entry: KikiwakeLogInput = {
            id: item.id,
            createdAt: item.created_at,
            date: item.date,
          };
          if (item.detail !== undefined) {
            entry.detail = item.detail;
            if (item.detail.includes('"kikiwake"')) kikiwakeSeen += 1;
          }
          logs.push(entry);
        }
        cursor = page.next_cursor;
      } while (cursor && pages < KIKIWAKE_MAX_FETCH_PAGES && kikiwakeSeen < 2);
      let resolved = resolveKikiwakeState(logs, todayLocal());
      if (resolved.source === "replay" && cursor) {
        // 系譜が壊れている: リプレイ用に残りも読む (§6.4)。頁数上限で打ち切っても
        // 和集合不変条件が active の後退を防ぐ (KIKIWAKE_MAX_FETCH_PAGES の @why)
        while (cursor && pages < KIKIWAKE_MAX_FETCH_PAGES) {
          const page = await fetchProgressLogs(familyId, personId, cursor, controller.signal, 100);
          pages += 1;
          for (const item of page.items) {
            const entry: KikiwakeLogInput = {
              id: item.id,
              createdAt: item.created_at,
              date: item.date,
            };
            if (item.detail !== undefined) entry.detail = item.detail;
            logs.push(entry);
          }
          cursor = page.next_cursor;
        }
        if (cursor) console.warn("[kikiwake] history fetch capped at", pages, "pages");
        resolved = resolveKikiwakeState(logs, todayLocal());
      }
      if (resolved.forked) {
        console.warn("[kikiwake] checkpoint lineage mismatch — replayed from full history");
      }
      resolvedRef.current = resolved;
      setActiveIds(resolved.state.active);
      setPhase({ kind: "start" });
    })().catch((err: unknown) => {
      if (controller.signal.aborted) return;
      console.error("[kikiwake] failed to load", err);
      setPhase({ kind: "broken" });
    });
    return () => controller.abort();
  }, [familyId, personId, navigate]);

  // だれのきき分けかをヘッダーに出す (どの面でも出す)。選び直すとその人のきき分けへ移る。
  const learnerOptions = useMemo(
    () => learners.map((l) => ({ id: l.person_id, label: memberLabel(l) })),
    [learners],
  );
  const selectLearner = useCallback(
    (id: string) => {
      if (id !== personId)
        navigate(`/study/${encodeURIComponent(familyId)}/kikiwake/${encodeURIComponent(id)}`);
    },
    [familyId, personId, navigate],
  );
  useHeaderLearner(
    learners.length > 0
      ? { options: learnerOptions, selectedId: personId, onSelect: selectLearner }
      : null,
  );

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      // AudioContext はブラウザ全体で生成数に上限があるため、画面を離れたら閉じる
      audioRef.current?.dispose();
      audioRef.current = null;
    };
  }, []);

  const later = useCallback((ms: number, fn: () => void) => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    if (ms <= 0) {
      fn();
      return;
    }
    timerRef.current = window.setTimeout(fn, ms);
  }, []);

  // ---- 保存 (完走・途中離脱とも同一ログ id の上書き = 二重計上なし、§4.1) ----
  const save = useCallback(
    async (partial: boolean, keepalive: boolean): Promise<void> => {
      const sess = sessionRef.current;
      if (!sess || sess.trials.length === 0 || sess.savedFinal) return;
      if (!partial) {
        sess.savedFinal = true;
        setSaveState("saving");
      }
      let trials = [...sess.trials];
      let muted = false;
      if (detectMutedSuspect(trials, cfg)) {
        trials = markAllNoCount(trials);
        muted = true;
      }
      const date = todayLocal();
      const after = applyKikiwakeSession(sess.baseState, { date, trials }, cfg);
      const detail = serializeKikiwakeDetail({
        v: 1,
        kind: "kikiwake",
        cfg: KIKIWAKE_CURRENT_CFG,
        seq: sess.seq,
        base: sess.base,
        ...(partial ? { partial: true as const } : {}),
        ...(muted ? { muted_suspect: true as const } : {}),
        trials,
        after,
      });
      const body: StudyProgressLogInput = {
        id: sess.logId,
        date,
        realm: "music",
        minutes: Math.max(1, Math.round((Date.now() - sess.startedMs) / 60000)),
        memo: buildKikiwakeMemo(trials),
        detail,
        ...(hasMaterialRef.current ? { material_id: ONKAN_MATERIAL_ID } : {}),
      };
      try {
        await postProgressLog(
          familyId,
          personId,
          body,
          keepalive ? { keepalive: true } : undefined,
        );
        if (!partial && resolvedRef.current) {
          resolvedRef.current = {
            state: after,
            source: "checkpoint",
            forked: false,
            nextSeq: sess.seq + 1,
            baseId: sess.logId,
          };
          setSaveState("done");
        }
      } catch (err) {
        if (!partial) {
          sess.savedFinal = false;
          setSaveState("failed");
        }
        console.error("[kikiwake] failed to save session", err);
      }
    },
    [familyId, personId],
  );

  // 途中離脱の保存 (S1)。バックグラウンド化・ページ破棄の直前に keepalive で送る
  useEffect(() => {
    const flush = () => {
      const sess = sessionRef.current;
      if (document.visibilityState === "visible" || !sess || sess.savedFinal) return;
      void save(true, true);
    };
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [save]);

  // ---- セッション進行 ----
  const totalOf = (sess: SessionRef): number => sess.plan.length + sess.katame.length;
  const chordAt = (sess: SessionRef, qi: number): ChordId => {
    const c = qi < sess.plan.length ? sess.plan[qi] : sess.katame[qi - sess.plan.length];
    if (c === undefined) throw new Error("kikiwake: question index out of range");
    return c;
  };

  const playQuestion = useCallback(
    (qi: number) => {
      const sess = sessionRef.current;
      if (!sess) return;
      if (qi >= totalOf(sess)) {
        setPhase({ kind: "finished" });
        void save(false, false);
        return;
      }
      if (qi === sess.plan.length && sess.katame.length > 0 && !sess.katameNoticed) {
        sess.katameNoticed = true;
        setPhase({ kind: "katame" });
        later(timings.noticeMs, () => playQuestion(qi));
        return;
      }
      setPhase({ kind: "playing", qi });
      void audio()
        .playChord(kikiwakeChord(chordAt(sess, qi)).notes)
        .then(() => {
          sess.enabledAt = performance.now();
          sess.replays = 0;
          setPhase({ kind: "answering", qi });
        });
    },
    [audio, later, save, timings.noticeMs],
  );

  const startSession = useCallback(() => {
    const resolved = resolvedRef.current;
    if (!resolved) return;
    setActiveIds(resolved.state.active);
    setSaveState("idle");
    const built = buildKikiwakeSession(resolved.state, todayLocal(), cfg, rng);
    sessionRef.current = {
      logId: newProgressLogId(),
      seq: resolved.nextSeq,
      base: resolved.baseId,
      baseState: resolved.state,
      plan: built.plan,
      katame: built.katame,
      intro: built.intro,
      trials: [],
      startedMs: Date.now(),
      enabledAt: 0,
      replays: 0,
      katameNoticed: false,
      savedFinal: false,
    };
    const introChord = built.intro;
    if (introChord) {
      const playIntro = (done: number) => {
        setPhase({ kind: "intro", done });
        if (done >= cfg.introPlays) {
          playQuestion(0);
          return;
        }
        void audio()
          .playChord(kikiwakeChord(introChord).notes)
          .then(() => playIntro(done + 1));
      };
      playIntro(0);
    } else {
      playQuestion(0);
    }
  }, [audio, playQuestion, rng]);

  const answer = useCallback(
    (x: ChordId) => {
      const sess = sessionRef.current;
      if (!sess || phase.kind !== "answering") return;
      const qi = phase.qi;
      const c = chordAt(sess, qi);
      const ms = Math.round(performance.now() - sess.enabledAt);
      const inKatame = qi >= sess.plan.length;
      const trial: KikiwakeTrial = {
        c,
        ms,
        ...(x !== c ? { x } : {}),
        ...(sess.replays > 0 ? { r: sess.replays } : {}),
        ...(ms < cfg.fastTapMs ? { n: true as const } : {}),
        ...(inKatame ? { k: true as const } : {}),
      };
      sess.trials.push(trial);
      if (x === c) {
        const praises = activeIds.length <= 1 ? FIRST_CHORD_PRAISES : PRAISES;
        setPhase({
          kind: "praise",
          qi,
          chord: c,
          praise: praises[Math.floor(rng() * praises.length)] ?? praises[0]!,
        });
        later(timings.praiseMs, () => playQuestion(qi + 1));
      } else {
        if (!inKatame) {
          sess.plan = reinforceAfterMiss(sess.plan, qi + 1, c, rng);
        }
        setPhase({ kind: "teach", qi, chord: c });
        // 正しい答をすぐ・やさしく教え、その和音をもう一度きかせる (R14)。これは出題ではない
        void audio()
          .playChord(kikiwakeChord(c).notes)
          .then(() => later(timings.teachMs, () => playQuestion(qi + 1)));
      }
    },
    [activeIds.length, audio, later, phase, playQuestion, rng, timings.praiseMs, timings.teachMs],
  );

  const replay = useCallback(() => {
    const sess = sessionRef.current;
    if (!sess || phase.kind !== "answering" || sess.replays >= cfg.maxReplays) return;
    sess.replays += 1;
    void audio().playChord(kikiwakeChord(chordAt(sess, phase.qi)).notes);
  }, [audio, phase]);

  const exitToStudy = useCallback(() => {
    const sess = sessionRef.current;
    const leave = () => navigate(`/study/${familyId}`);
    if (sess && sess.trials.length > 0 && !sess.savedFinal) {
      void save(true, false).finally(leave);
    } else {
      leave();
    }
  }, [familyId, navigate, save]);

  // ---- 表示 ----
  const activeChords = activeIds.map((id) => kikiwakeChord(id));

  const answering = phase.kind === "answering";
  const currentChord =
    phase.kind === "praise" || phase.kind === "teach" ? kikiwakeChord(phase.chord) : null;

  if (phase.kind === "boot") {
    return (
      <div className="kikiwake-screen" data-phase="boot">
        <p className="kikiwake-message">じゅんびちゅう…</p>
      </div>
    );
  }
  if (phase.kind === "broken") {
    return (
      <div className="kikiwake-screen" data-phase="broken">
        <p className="kikiwake-message">
          よみこめませんでした。でんぱの よいところで もういちど ためしてね
        </p>
        <Link className="kikiwake-exit-link" to={`/study/${familyId}`}>
          もどる
        </Link>
      </div>
    );
  }

  return (
    <div className="kikiwake-screen" data-phase={phase.kind}>
      {phase.kind !== "finished" && phase.kind !== "start" && (
        <div className="kikiwake-corner">
          <LongPressButton label="おわる" hint="おとなのひとと ながおし" onFire={exitToStudy} />
        </div>
      )}

      {phase.kind === "start" && (
        <div className="kikiwake-center">
          <p className="kikiwake-learner">{learnerName}の</p>
          <h1 className="kikiwake-title">きき分けあそび</h1>
          <button
            type="button"
            className="kikiwake-big-btn"
            onClick={() => {
              setPhase({ kind: "soundcheck", playing: true });
              void audio()
                .unlock()
                .then(() => audio().playChord(kikiwakeChord("domiso").notes))
                .then(() => setPhase({ kind: "soundcheck", playing: false }));
            }}
          >
            はじめる
          </button>
          <Link className="kikiwake-exit-link" to={`/study/${familyId}`}>
            もどる
          </Link>
        </div>
      )}

      {phase.kind === "soundcheck" && (
        <div className="kikiwake-center">
          <p className="kikiwake-message">ピアノの おとが きこえたら おしてね</p>
          <button
            type="button"
            className="kikiwake-big-btn"
            disabled={phase.playing}
            onClick={startSession}
          >
            きこえた！
          </button>
          <button
            type="button"
            className="kikiwake-sub-btn"
            disabled={phase.playing}
            onClick={() => {
              setPhase({ kind: "soundcheck", playing: true });
              void audio()
                .playChord(kikiwakeChord("domiso").notes)
                .then(() => setPhase({ kind: "soundcheck", playing: false }));
            }}
          >
            もういちど ならす
          </button>
        </div>
      )}

      {phase.kind === "intro" && sessionRef.current?.intro && (
        <div className="kikiwake-center">
          <p className="kikiwake-message">
            あたらしい いろ！ <strong>{kikiwakeChord(sessionRef.current.intro).colorKana}</strong>{" "}
            だよ。きいてね
          </p>
          <div className="kikiwake-intro-dots" aria-hidden="true">
            {Array.from({ length: cfg.introPlays }, (_, i) => (
              <span key={i} className={i < phase.done ? "is-done" : ""} />
            ))}
          </div>
        </div>
      )}

      {(phase.kind === "playing" ||
        phase.kind === "answering" ||
        phase.kind === "praise" ||
        phase.kind === "teach" ||
        phase.kind === "katame") && (
        <div className="kikiwake-game">
          <p className="kikiwake-prompt">
            {phase.kind === "playing" && "よく きいてね…"}
            {phase.kind === "answering" &&
              (activeIds.length <= 1 ? "こえに だして いってみよう" : "どの いろ かな？")}
            {phase.kind === "praise" && currentChord && (
              <>
                {phase.praise} <strong>{currentChord.colorKana}</strong>！
              </>
            )}
            {phase.kind === "teach" && currentChord && (
              <>
                いまのは <strong>{currentChord.colorKana}</strong> だよ
              </>
            )}
            {phase.kind === "katame" && sessionRef.current?.katame[0] && (
              <>
                さいごに <strong>{kikiwakeChord(sessionRef.current.katame[0]).colorKana}</strong> を
                たくさん きくよ
              </>
            )}
          </p>
          <div className="kikiwake-flags">
            {activeChords.map((chord) => (
              <button
                key={chord.id}
                type="button"
                className={`kikiwake-flag${phase.kind === "teach" && currentChord?.id === chord.id ? " is-answer" : ""}`}
                style={{ backgroundColor: chord.css, color: chord.textOnColor }}
                disabled={!answering}
                onClick={() => answer(chord.id)}
              >
                <span className="kikiwake-flag-kana">{chord.colorKana}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="kikiwake-sub-btn"
            disabled={!answering || (sessionRef.current?.replays ?? 0) >= cfg.maxReplays}
            onClick={replay}
          >
            もういちど きく
          </button>
        </div>
      )}

      {phase.kind === "finished" && (
        <div className="kikiwake-center">
          <h1 className="kikiwake-title">おしまい！</h1>
          <p className="kikiwake-message">きょうも よく きけたね</p>
          {saveState === "failed" && (
            <p className="kikiwake-save-error">
              きろくが のこせなかったよ
              <button
                type="button"
                className="kikiwake-sub-btn"
                onClick={() => void save(false, false)}
              >
                もういちど のこす
              </button>
            </p>
          )}
          <button type="button" className="kikiwake-big-btn" onClick={startSession}>
            もういっかい
          </button>
          <LongPressButton label="おわる" hint="おとなのひとと ながおし" onFire={exitToStudy} />
        </div>
      )}
    </div>
  );
}
